import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";

const HEADER_REGIONS = "__H_REGIONS";
const HEADER_COUNTRIES = "__H_COUNTRIES";
const SEPARATOR = "__SEP__";
const HEADERS = { [HEADER_REGIONS]: "Regions", [HEADER_COUNTRIES]: "Countries" };
const isHeader = (o) => o === HEADER_REGIONS || o === HEADER_COUNTRIES;
const isSeparator = (o) => o === SEPARATOR;

export const MultiSelect = ({ options, selected = [], onChange, placeholder = "Select...", testId }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (opt) => {
    if (isHeader(opt) || isSeparator(opt)) return;
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
    setFilter("");
  };

  const remove = (opt) => onChange(selected.filter((s) => s !== opt));

  const filtered = options.filter((o) => {
    if (isHeader(o) || isSeparator(o)) return filter === "";
    return o.toLowerCase().includes(filter.toLowerCase());
  });

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="aurora-input flex items-center justify-between text-left"
        data-testid={`${testId}-trigger`}
      >
        <span className={selected.length === 0 ? "text-[#6B5E4A]/60" : "text-[#190F08]"}>
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
        <ChevronDown size={16} className="text-[#A88059]" />
      </button>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`${testId}-chips`}>
          {selected.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-2 px-3 py-1 text-xs border border-[#A88059]/60 text-[#6B4D2B] font-body"
            >
              {s}
              <button
                type="button"
                onClick={() => remove(s)}
                className="hover:text-[#190F08]"
                data-testid={`${testId}-remove-${s}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <div
          className="absolute z-40 mt-2 w-full max-h-72 overflow-auto bg-[#FAF5EE] border border-[#A88059] shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          <input
            autoFocus
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search..."
            className="aurora-input px-3 py-2 m-2"
            style={{ width: "calc(100% - 16px)" }}
            data-testid={`${testId}-search`}
          />
          {filtered.map((opt, idx) => {
            if (isHeader(opt)) {
              return (
                <div
                  key={`h-${opt}-${idx}`}
                  className="px-4 py-2 mt-1 text-[10px] font-heading tracking-[0.22em] uppercase text-[#A88059] border-t border-b border-[#A88059]/40 bg-[#A88059]/5"
                  data-testid={`${testId}-header-${HEADERS[opt]}`}
                >
                  {HEADERS[opt]}
                </div>
              );
            }
            if (isSeparator(opt)) {
              return <div key={`s-${idx}`} className="border-t border-[#A88059]/40 my-1" aria-hidden="true" />;
            }
            const isSel = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-[#A88059]/10 transition-colors ${
                  isSel ? "text-[#6B4D2B]" : "text-[#190F08]"
                }`}
                data-testid={`${testId}-option-${opt}`}
              >
                <span className="inline-block w-4 mr-2 font-heading">{isSel ? "✓" : ""}</span>
                {opt}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-4 py-3 text-sm text-[#6B5E4A]">No matches</div>
          )}
        </div>
      )}
    </div>
  );
};

export const SingleSelect = ({ options, value = "", onChange, placeholder = "Select...", testId, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus the search box when the dropdown opens
  useEffect(() => {
    if (open && searchable && searchRef.current) {
      searchRef.current.focus();
    }
    if (!open) setFilter("");
  }, [open, searchable]);

  const q = filter.trim().toLowerCase();
  const matches = (o) => !q || (typeof o === "string" && o.toLowerCase().includes(q));

  // Group consecutive entries by their preceding header for visible subheadings inside the popover.
  // When filtering, only include headers/separators that still precede at least one matching option.
  const items = [];
  let pendingHeader = null;
  let pendingSep = false;
  options.forEach((o) => {
    if (isHeader(o)) {
      pendingHeader = HEADERS[o];
    } else if (isSeparator(o)) {
      pendingSep = true;
    } else if (matches(o)) {
      if (pendingSep) {
        items.push({ kind: "sep" });
        pendingSep = false;
      }
      if (pendingHeader) {
        items.push({ kind: "header", label: pendingHeader });
        pendingHeader = null;
      }
      items.push({ kind: "opt", value: o });
    }
  });

  const pick = (opt) => {
    onChange(opt);
    setOpen(false);
    setFilter("");
  };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="aurora-input flex items-center justify-between text-left"
        data-testid={`${testId}-trigger`}
      >
        <span className={value ? "text-[#190F08]" : "text-[#6B5E4A]/60"}>
          {value || placeholder}
        </span>
        <ChevronDown size={16} className="text-[#A88059]" />
      </button>

      {open && (
        <div
          className="absolute z-40 mt-2 w-full max-h-72 overflow-auto bg-[#FAF5EE] border border-[#A88059] shadow-xl"
          data-testid={`${testId}-dropdown`}
        >
          {searchable && (
            <div className="sticky top-0 z-10 bg-[#FAF5EE] border-b border-[#A88059]/40 p-2">
              <input
                ref={searchRef}
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 text-sm bg-white/60 border border-[#A88059]/40 focus:outline-none focus:border-[#A88059] font-body text-[#190F08]"
                data-testid={`${testId}-search`}
              />
            </div>
          )}
          {items.length === 0 && (
            <div className="px-4 py-3 text-sm text-[#6B5E4A]/70 font-body italic">
              No matches
            </div>
          )}
          {items.map((it, i) => {
            if (it.kind === "header") {
              return (
                <div
                  key={`h-${i}`}
                  className="px-4 py-2 mt-1 text-[10px] font-heading tracking-[0.22em] uppercase text-[#A88059] border-t border-b border-[#A88059]/40 bg-[#A88059]/5"
                >
                  {it.label}
                </div>
              );
            }
            if (it.kind === "sep") {
              return <div key={`s-${i}`} className="border-t border-[#A88059]/40 my-1" aria-hidden="true" />;
            }
            const isActive = value === it.value;
            return (
              <button
                key={it.value}
                type="button"
                onClick={() => pick(it.value)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#A88059] text-[#FAF5EE]"
                    : "text-[#190F08] hover:bg-[#F0E6D6]"
                }`}
                data-testid={`${testId}-option-${it.value}`}
              >
                {it.value}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
